import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { Calendar, RefreshCw } from 'lucide-react';
import AppLayout from '../layout/AppLayout';
import { useGoogleCalendar } from '../../hooks/useGoogleCalendar';
import toast from 'react-hot-toast';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';

interface CalendarSettings {
  googleCalendar: {
    enabled: boolean;
    syncEnabled: boolean;
    syncDirection: 'both' | 'import' | 'export';
    lastSync?: string;
    calendars: {
      id: string;
      name: string;
      selected: boolean;
    }[];
  };
}

export default function CalendarSettingsPage() {
  useDocumentTitle('Calendar Settings');
  const [settings, setSettings] = useState<CalendarSettings>({
    googleCalendar: {
      enabled: false,
      syncEnabled: false,
      syncDirection: 'both',
      calendars: []
    }
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { initiateAuth, syncCalendars, loading: googleLoading } = useGoogleCalendar();

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const user = auth.currentUser;
        if (!user) return;

        const docRef = doc(db, 'users', user.uid);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists() && docSnap.data().calendarSettings) {
          setSettings(docSnap.data().calendarSettings);
        }
      } catch (error) {
        console.error('Error loading calendar settings:', error);
        toast.error('Failed to load calendar settings');
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, []);

  const handleSave = async () => {
    try {
      setSaving(true);
      const user = auth.currentUser;
      if (!user) return;

      await setDoc(doc(db, 'users', user.uid), {
        calendarSettings: settings
      }, { merge: true });

      toast.success('Calendar settings saved successfully');
    } catch (error) {
      console.error('Error saving calendar settings:', error);
      toast.error('Failed to save calendar settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSync = async () => {
    const selectedCalendars = settings.googleCalendar.calendars
      .filter(cal => cal.selected)
      .map(cal => cal.id);

    await syncCalendars(selectedCalendars);
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-full">
          <div className="text-text-secondary">Loading calendar settings...</div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="p-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-4 mb-6">
            <Calendar className="w-8 h-8 text-primary-500" />
            <div>
              <h1 className="text-2xl font-semibold text-text-primary">Calendar Settings</h1>
              <p className="text-text-secondary">Manage your calendar integrations and preferences</p>
            </div>
          </div>

          <div className="space-y-6">
            {/* Google Calendar Integration */}
            <div className="bg-surface-light rounded-lg p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-lg font-semibold text-text-primary">Google Calendar</h2>
                  <p className="text-text-secondary">
                    Sync your events with Google Calendar
                  </p>
                </div>
                {settings.googleCalendar.enabled ? (
                  <button
                    onClick={handleSync}
                    disabled={googleLoading}
                    className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 
                             transition-colors disabled:opacity-50 disabled:cursor-not-allowed 
                             flex items-center gap-2"
                  >
                    <RefreshCw className={`w-5 h-5 ${googleLoading ? 'animate-spin' : ''}`} />
                    {googleLoading ? 'Syncing...' : 'Sync Now'}
                  </button>
                ) : (
                  <button
                    onClick={initiateAuth}
                    disabled={googleLoading}
                    className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 
                             transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Connect
                  </button>
                )}
              </div>

              {settings.googleCalendar.enabled && (
                <div className="space-y-4">
                  <div>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={settings.googleCalendar.syncEnabled}
                        onChange={(e) => setSettings({
                          ...settings,
                          googleCalendar: {
                            ...settings.googleCalendar,
                            syncEnabled: e.target.checked
                          }
                        })}
                        className="w-4 h-4 text-primary-500 border-border-light rounded 
                                 focus:ring-primary-500"
                      />
                      <span className="text-text-primary">Enable automatic sync</span>
                    </label>
                    <p className="text-sm text-text-secondary ml-6 mt-1">
                      Automatically sync events between Campus Connect and Google Calendar
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-2">
                      Sync Direction
                    </label>
                    <select
                      value={settings.googleCalendar.syncDirection}
                      onChange={(e) => setSettings({
                        ...settings,
                        googleCalendar: {
                          ...settings.googleCalendar,
                          syncDirection: e.target.value as 'both' | 'import' | 'export'
                        }
                      })}
                      className="w-full px-4 py-2 rounded-lg border border-border-light focus:border-primary-500 
                               focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
                    >
                      <option value="both">Two-way sync</option>
                      <option value="import">Import from Google Calendar only</option>
                      <option value="export">Export to Google Calendar only</option>
                    </select>
                  </div>

                  {settings.googleCalendar.calendars.length > 0 && (
                    <div>
                      <h3 className="text-sm font-medium text-text-secondary mb-2">
                        Select Calendars to Sync
                      </h3>
                      <div className="space-y-2">
                        {settings.googleCalendar.calendars.map((calendar) => (
                          <label key={calendar.id} className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={calendar.selected}
                              onChange={(e) => setSettings({
                                ...settings,
                                googleCalendar: {
                                  ...settings.googleCalendar,
                                  calendars: settings.googleCalendar.calendars.map(cal => 
                                    cal.id === calendar.id 
                                      ? { ...cal, selected: e.target.checked }
                                      : cal
                                  )
                                }
                              })}
                              className="w-4 h-4 text-primary-500 border-border-light rounded 
                                       focus:ring-primary-500"
                            />
                            <span className="text-text-primary">{calendar.name}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                  {settings.googleCalendar.lastSync && (
                    <p className="text-sm text-text-secondary">
                      Last synced: {new Date(settings.googleCalendar.lastSync).toLocaleString()}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Save Button */}
            <div className="flex justify-end">
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-6 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 
                         transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}